import { Link } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { getDescriptionForPath } from '../../config/configEntry';
import { fetchStaticJson } from '../../config/fetchStaticJson';
import DiscoveryButtonsEditor from './DiscoveryButtonsEditor';
import BotIconUploader from './BotIconUploader';
import classes from './AdminSettingsPanel.module.css';

const ADMIN_TEXT_FIELD_KEYS = [
  'botTitle',
  'botSubTitle',
  'typeHereText',
  'urlClickText',
  'nonAuthText',
];

const ADMIN_COLOR_FIELD_KEYS = [
  'backgroundGradientStart',
  'backgroundGradientEnd',
  'backgroundGradientSidebarStart',
  'backgroundGradientSidebarEnd',
  'headerGradientStart',
  'headerGradientMid',
  'headerGradientEnd',
  'surface',
  'cookiesSurface',
  'primary',
  'iconSendStroke',
  'iconSendFill',
  'onPrimary',
  'userBubbleBackground',
  'userBubbleText',
  'modelBubbleBackground',
  'modelBubbleText',
  'loaderPrimary',
  'loaderSecondary',
  'discoveryButtonBorder',
  'discoveryButtonBackground',
  'discoveryButtonText',
  'sessionItemSelectedBackground',
  'sessionItemDateText',
  'sessionItemTopicText',
  'sendButtonDisabledBackground',
  'messageInputBackground',
  'messageInputBorder',
  'sidebarFooterBorder',
  'sidebarFooterText',
  'sidebarHeaderText',
  'iconCloseStroke',
  'iconDragStroke',
  'iconChatsStroke',
  'iconExpandStroke',
  'iconSidebarCollapseStroke',
  'iconSidebarFooterStroke',
  'authButtonText',
  'iconChatTextStroke',
  'iconNewConversationStroke',
  'greetingText',
];

function colorKeysForDraft(theme) {
  const colorKeys = Object.keys(theme?.colors || {});
  const colorKeySet = new Set(colorKeys);
  const orderedKeys = ADMIN_COLOR_FIELD_KEYS.filter((key) => colorKeySet.has(key));
  const extraKeys = colorKeys.filter((key) => !ADMIN_COLOR_FIELD_KEYS.includes(key));

  return [...orderedKeys, ...extraKeys];
}

function descriptionsUrl() {
  const base = (process.env.PUBLIC_URL || '').replace(/\/$/, '');
  return `${base}/config/descriptions.json`;
}

export default function AdminSettingsPanel({
  domainName,
  botName,
  draft,
  setDraft,
  discoveryButtons,
  onDiscoveryButtonsChange,
  originalBotIconUrl,
  onExportConfig,
  onExportZip,
  exportingZip,
  onResetDraft,
  exportError,
  setExportError,
  draftRestoredNotice,
  modalContainer,
}) {
  const [colorKeys, setColorKeys] = useState([]);
  const [descriptions, setDescriptions] = useState({});

  useEffect(() => {
    fetchStaticJson(descriptionsUrl())
      .then((data) => {
        if (data && typeof data === 'object') setDescriptions(data);
      })
      .catch(() => {
        setDescriptions({});
      });
  }, []);

  useEffect(() => {
    setColorKeys(colorKeysForDraft(draft.theme));
  }, [draft.theme]);

  const setColor = (key, value) => {
    setDraft((d) => ({
      ...d,
      theme: {
        ...d.theme,
        colors: {
          ...d.theme?.colors,
          [key]: value,
        },
      },
    }));
  };

  const setTextField = (key, value) => {
    setDraft((d) => ({ ...d, [key]: value }));
  };

  return (
    <div className={classes.panel}>
      <Link to="/admin" className={classes.backLink}>
        ← חזרה לרשימת בוטים
      </Link>

      {draftRestoredNotice ? (
        <div className={classes.hint}>טיוטה מקומית שוחזרה</div>
      ) : null}

      <div className={classes.toolbar}>
        <button type="button" className={classes.btnPrimary} onClick={onExportConfig}>
          הורד JSON
        </button>
        <button
          type="button"
          className={classes.btn}
          onClick={onExportZip}
          disabled={exportingZip}
        >
          {exportingZip ? 'מייצא...' : 'הורד ZIP'}
        </button>
        <button type="button" className={classes.btn} onClick={onResetDraft}>
          איפוס לקובץ המקורי
        </button>
      </div>

      <div className={classes.sectionTitle}>התנהגות</div>
      <div className={classes.field}>
        <label className={classes.checkboxRow} htmlFor="enableBotMessageCopy">
          <input
            id="enableBotMessageCopy"
            type="checkbox"
            checked={draft.enableBotMessageCopy === true}
            onChange={(e) =>
              setDraft((d) => ({
                ...d,
                enableBotMessageCopy: e.target.checked,
              }))
            }
          />
          <span className={classes.checkboxLabel}>
            {getDescriptionForPath(
              descriptions,
              'enableBotMessageCopy',
              'enableBotMessageCopy'
            )}
          </span>
        </label>
      </div>

      <div className={classes.sectionTitle}>טקסטים ושדות</div>
      {ADMIN_TEXT_FIELD_KEYS.map((key) => (
        <div key={key} className={classes.field}>
          <label className={classes.label} htmlFor={key}>
            {getDescriptionForPath(descriptions, key, key)}
          </label>
          <input
            id={key}
            className={classes.textInput}
            value={draft[key] ?? ''}
            onChange={(e) => setTextField(key, e.target.value)}
          />
        </div>
      ))}

      <BotIconUploader
        domainName={domainName}
        botName={botName}
        iconUrl={draft.botIconUrl ?? ''}
        originalBotIconUrl={originalBotIconUrl}
        onIconUrlChange={(url) => setTextField('botIconUrl', url)}
        descriptions={descriptions}
      />

      <DiscoveryButtonsEditor
        botName={botName}
        draft={draft}
        buttons={discoveryButtons}
        onChange={onDiscoveryButtonsChange}
        descriptions={descriptions}
        modalContainer={modalContainer}
      />

      {exportError ? (
        <span className={classes.hint} style={{ color: '#f87171' }}>
          {exportError}
        </span>
      ) : null}

      <div className={classes.sectionTitle}>צבעים ({botName})</div>
      <div className={classes.colorsGrid}>
        {colorKeys.map((key) => {
          const hex = draft.theme?.colors?.[key] ?? '#000000';
          const path = `theme.colors.${key}`;
          const label = getDescriptionForPath(descriptions, key, path);
          return (
            <div key={key} className={classes.field}>
              <label className={classes.label} htmlFor={`c-${key}`}>
                {label}
              </label>
              <div className={classes.colorRow}>
                <input
                  id={`c-${key}`}
                  type="color"
                  className={classes.colorPicker}
                  value={normalizeHex(hex || '#000000')}
                  onChange={(e) => setColor(key, e.target.value)}
                />
                <input
                  className={classes.textInput}
                  style={{ flex: 1 }}
                  value={hex}
                  placeholder="#"
                  onChange={(e) => setColor(key, e.target.value)}
                />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function normalizeHex(val) {
  if (typeof val !== 'string') return '#000000';
  const s = val.trim();
  if (/^#[0-9A-Fa-f]{6}$/.test(s)) return s;
  if (/^#[0-9A-Fa-f]{3}$/.test(s)) {
    return (
      '#' +
      s[1] +
      s[1] +
      s[2] +
      s[2] +
      s[3] +
      s[3]
    ).toLowerCase();
  }
  return '#000000';
}
